import ChatRounded from '@mui/icons-material/ChatRounded';
import DeliveryDiningRounded from '@mui/icons-material/DeliveryDiningRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import SettingsRounded from '@mui/icons-material/SettingsRounded';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import { businessDate } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { FOODPANDA_PINK, useFoodpandaSettings, useFoodpandaStatus } from './api';

const ASK = '/support?message=' + encodeURIComponent('Hi, I want the Foodpanda feature');
const FEATURE =
  'Take Foodpanda orders in PackPilot: accept them, print the kitchen ticket, and they become sales orders and invoices by themselves.';

/**
 * The Foodpanda add-on on the Subscription page, like the WhatsApp card. Not enabled: dimmed with
 * a lock and a quick way to ask for it. Enabled: its period and grace, the shop's on/off switch
 * (off refuses new orders) and the way to its set-up.
 */
export function FoodpandaCard() {
  const { can } = useAuth();
  const { format } = useShop();
  const notify = useNotify();
  const status = useFoodpandaStatus();
  const save = useFoodpandaSettings();
  const plugin = status.data?.plugin;
  if (!plugin) return null;
  const icon = (dim: boolean) => (
    <DeliveryDiningRounded sx={{ fontSize: 40, color: FOODPANDA_PINK, opacity: dim ? 0.45 : 1 }} />
  );

  if (plugin.state === 'NOT_ENABLED')
    return (
      <Tooltip
        title="Contact us to add Foodpanda for a small monthly fee."
        placement="top"
        describeChild
      >
        <Card>
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
              {icon(true)}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography variant="h6">Foodpanda</Typography>
                  <Chip size="small" icon={<LockRounded />} label="Add-on" variant="outlined" />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {FEATURE}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Contact us to add it for a small monthly fee.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ChatRounded />}
                  component={RouterLink}
                  to={ASK}
                  sx={{ mt: 1.5 }}
                >
                  Ask for Foodpanda
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Tooltip>
    );

  const chip =
    plugin.state === 'ACTIVE'
      ? { label: 'Active', color: 'success' as const }
      : plugin.state === 'GRACE'
        ? { label: 'In grace', color: 'warning' as const }
        : { label: 'Ended', color: 'error' as const };
  const day = (iso: string | null) => (iso ? businessDate(iso.slice(0, 10), format) : '—');
  const canManage = can('foodpanda.manage');
  const connection = status.data?.connection;

  return (
    <Card>
      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
          {icon(false)}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="h6">Foodpanda</Typography>
              <Chip size="small" color={chip.color} label={chip.label} variant="outlined" />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {plugin.state === 'EXPIRED'
                ? 'Foodpanda subscription ended, grace used. Contact PackBytes to renew it.'
                : `Paid through ${day(plugin.paidThrough)} · works until ${day(plugin.graceEndsAt)} with grace`}
            </Typography>
            {(canManage || can('foodpanda.orders')) && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {!connection
                  ? 'Not set up yet.'
                  : connection.environment === 'SIMULATOR'
                    ? 'Simulator mode: try it with test orders.'
                    : connection.environment === 'STAGING'
                      ? 'Connected to Foodpanda’s test system.'
                      : 'Taking live Foodpanda orders.'}
              </Typography>
            )}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ mt: 1.5, alignItems: { sm: 'center' } }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={plugin.shopEnabled}
                    disabled={!canManage || save.isPending}
                    onChange={(e) =>
                      save.mutate({ shopEnabled: e.target.checked }, { onError: notify.error })
                    }
                  />
                }
                label={plugin.shopEnabled ? 'Taking orders' : 'Switched off'}
              />
              {canManage && (
                <Button
                  size="small"
                  startIcon={<SettingsRounded />}
                  component={RouterLink}
                  to="/foodpanda?tab=setup"
                >
                  {connection ? 'Foodpanda set-up' : 'Set up Foodpanda'}
                </Button>
              )}
              {plugin.state !== 'ACTIVE' && (
                <Button size="small" startIcon={<ChatRounded />} component={RouterLink} to={ASK}>
                  Contact us
                </Button>
              )}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
